import { BarcodeScanner, SupportedFormat } from '@capacitor-community/barcode-scanner';
import { isNativeNotWeb } from '@/utils/platform-check';
import { scannerPayloadFromUrl, ssoApprovalFromUrl } from '@/utils/scan-route';

/**
 * General-purpose QR scanner utility.
 * Wraps the same @capacitor-community/barcode-scanner plugin used by PhotoDrop.
 */

export async function checkScannerPermission() {
  if (!isNativeNotWeb()) {
    return { granted: false, reason: 'web' };
  }

  const status = await BarcodeScanner.checkPermission({ force: true });
  if (status.granted) {
    return { granted: true };
  }

  if (status.denied || status.restricted) {
    return { granted: false, reason: 'denied' };
  }

  return { granted: false, reason: 'required' };
}

export async function isSupported() {
  return isNativeNotWeb();
}

/**
 * Runs the native scanner and hands back the raw QR string, unparsed.
 *
 * Exists so a caller that owns its own payload schema does not have to route
 * through parseQrData's hogc.one deep-link / SSO-approval union to reach the
 * string it just scanned: receive-photos matches a fotoshare.co URL with a
 * regex, and the Next Steps device-approval QR (#1693) carries a secret that
 * must never reach qr-handler's unsupported-type fallback, which echoes what it
 * received into a toast. Reading `.raw` off parseQrData's return is not a
 * substitute — a JSON QR carrying a `type` field is returned verbatim with no
 * `raw` property at all, so the caller's regex would silently test `undefined`.
 *
 * This and startScan() deliberately share one teardown path. A page that owned
 * its own hide/show pairing is exactly how #1226 happened: one rejection from
 * the plugin left the webview transparent with no camera behind it, an
 * invisible layer swallowing every tap on the page underneath.
 */
export async function startScanRaw() {
  if (!isNativeNotWeb()) {
    throw new Error('QR scanning is only available on native devices.');
  }

  const permission = await checkScannerPermission();
  if (!permission.granted) {
    throw new Error(
      permission.reason === 'denied'
        ? 'Camera permission was denied. Please enable it in your device settings.'
        : 'Camera permission is required to scan QR codes.',
    );
  }

  try {
    setScannerActive(true);
    await BarcodeScanner.hideBackground();

    const result = await BarcodeScanner.startScan({
      targetedFormats: [SupportedFormat.QR_CODE],
    });

    return result.hasContent && result.content ? result.content : null;
  } finally {
    await cleanupScannerView();
  }
}

/**
 * Runs the native scanner and parses the result as an app QR. Delegates the
 * camera lifecycle to startScanRaw so the hide/show pairing lives in exactly
 * one place; this function is only the parse step on top of it.
 */
export async function startScan() {
  const raw = await startScanRaw();
  return raw === null ? null : parseQrData(raw);
}

export async function stopScan() {
  try {
    await BarcodeScanner.stopScan();
  } catch {
    // Scanner may not be active
  }
  await cleanupScannerView();
}

function setScannerActive(isActive) {
  const method = isActive ? 'add' : 'remove';
  document.querySelector('body')?.classList[method]('scanner-active');
  document.querySelector('ion-app')?.classList[method]('scanner-active');
}

async function cleanupScannerView() {
  try {
    await BarcodeScanner.showBackground();
  } catch {
    // Scanner may not be active
  } finally {
    setScannerActive(false);
  }
}

export function parseQrData(rawValue) {
  // Checked before the /scan/ contract: an SSO approval QR is an in-app deep
  // link to the approval page, not a backend scanner call, and its host would
  // otherwise fall through to 'unknown'.
  const ssoApproval = ssoApprovalFromUrl(rawValue);
  if (ssoApproval) {
    return { type: 'sso-approval', ssoApproval, raw: rawValue };
  }

  const scannerPayload = scannerPayloadFromUrl(rawValue);
  if (scannerPayload) {
    return { type: 'scanner', scanner: scannerPayload, raw: rawValue };
  }

  try {
    const data = JSON.parse(rawValue);
    if (data && typeof data === 'object' && data.type) {
      return data;
    }
    // Valid JSON but missing type field
    return { type: 'unknown', raw: rawValue, data };
  } catch {
    // Not JSON - return as raw string
    return { type: 'unknown', raw: rawValue };
  }
}
